import { format as dFormatDate } from 'date-fns'
import {
	da as daLocaleDateFns,
	enUS,
	is as isLocaleDateFns,
	nb as nbLocaleDateFns,
	sv as svLocaleDateFns,
	type Locale as DateFnsLocale
} from 'date-fns/locale'

/**
 * This file was auto-generated
 * Do not edit manually
 */
import type { Locales } from './i18n-types'

// ============= Built-in Formatters =============
// ============= Formatter Types =============
export type Amount = { value: number; precision: number }

export type TranslationAmount = {
	currencyCode: string | undefined
	amount: Amount | number | undefined
	unitToDivideWith?: string
	intlOptions?: Intl.NumberFormatOptions
	decimals?: number
}

export type DateInput = Date | number

export type CustomDateInput = {
	date: DateInput
	formatString: string
	capitalize?: boolean
}

export type PluralForms = {
	zero?: string
	one?: string
	two?: string
	few?: string
	many?: string
	other: string
}

// ============= Formatter Utilities =============
const isAmount = (value: unknown): value is Amount => {
	return (
		!!value &&
		typeof value === 'object' &&
		'value' in value &&
		'precision' in value &&
		typeof (value as Amount).value === 'number' &&
		typeof (value as Amount).precision === 'number'
	)
}

const isTranslationAmount = (value: unknown): value is TranslationAmount => {
	if (!value || typeof value !== 'object') return false
	if (!('currencyCode' in value) || !('amount' in value)) return false
	const amount = (value as TranslationAmount).amount
	return typeof amount === 'number' || isAmount(amount)
}

const amountToNumber = (amount: Amount | number): number => {
	if (typeof amount === 'number') return amount
	return amount.value / Math.pow(10, amount.precision)
}

const getDateFnsLocale = (locale: Locales): DateFnsLocale => {
	switch (locale as string) {
		case 'is':
			return isLocaleDateFns
		case 'nb':
			return nbLocaleDateFns
		case 'da':
			return daLocaleDateFns
		case 'sv':
			return svLocaleDateFns
		default:
			return enUS
	}
}

const capitalizeStr = (value: string): string =>
	value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1)

/**
 * Plural helper using Intl.PluralRules
 * Maps 0 and '0' to 'zero' instead of 'other'
 */
export const plural = (locale: Locales, count: number, forms: PluralForms): string => {
	// Handle zero specially
	if (count === 0 && forms.zero !== undefined) {
		// Replace ?? with the count
		return forms.zero.replace(/\?\?/g, String(count))
	}

	// Use Intl.PluralRules for other cases
	const pluralRules = new Intl.PluralRules(locale)
	const rule = pluralRules.select(count) as keyof Omit<PluralForms, 'other'>

	// Get the form, fallback to 'other'
	const form = forms[rule] ?? forms.other

	// Replace ?? with the count
	return form.replace(/\?\?/g, String(count))
}

// ============= Amount Formatters =============
type AmountFormatOptions = { suffixCurrencyCode?: boolean; onlyAmount?: boolean }

const formatAmount = (locale: Locales, input: TranslationAmount, opts: AmountFormatOptions): string => {
	if (!isTranslationAmount(input) || input.amount === undefined || !input.currencyCode) {
		return '-'
	}
	const { currencyCode } = input
	const intlOptions: Intl.NumberFormatOptions = {
		style: 'currency',
		currency: currencyCode,
		currencyDisplay: opts.onlyAmount ? 'code' : 'symbol',
		...(input.intlOptions ?? {})
	}
	let value = amountToNumber(input.amount)
	if (input.decimals !== undefined) {
		value = Math.round(value * Math.pow(10, input.decimals)) / Math.pow(10, input.decimals)
		intlOptions.minimumFractionDigits = input.decimals
		intlOptions.maximumFractionDigits = input.decimals
	}
	if (Number.isNaN(value)) return '-'
	let formatted = new Intl.NumberFormat(locale, intlOptions).format(value)
	if (input.unitToDivideWith) {
		formatted = `${formatted}/${input.unitToDivideWith}`
	}
	if (opts.onlyAmount) {
		formatted = formatted.replace(currencyCode, '').trim()
	}
	if (opts.suffixCurrencyCode) {
		formatted = `${formatted} (${currencyCode})`
	}
	return formatted
}

export const amountFormatter = (locale: Locales, input: TranslationAmount): string => formatAmount(locale, input, {})

export const amountWithCodeFormatter = (locale: Locales, input: TranslationAmount): string =>
	formatAmount(locale, input, { suffixCurrencyCode: true })

export const amountOnlyFormatter = (locale: Locales, input: TranslationAmount): string =>
	formatAmount(locale, input, { onlyAmount: true })

// ============= Date Formatters =============
const parseDate = (value: DateInput): Date => {
	if (typeof value === 'number') return new Date(value)
	return value
}

const formatDateIntl = (
	date: Date,
	locale: string,
	options: Intl.DateTimeFormatOptions,
	opts: { capitalize?: boolean } = {}
): string => {
	const formatted = new Intl.DateTimeFormat(locale, options).format(date)
	return opts.capitalize ? capitalizeStr(formatted) : formatted
}

export const shortFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, { day: 'numeric', month: 'short' })
}

export const shortWithDateFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, { day: 'numeric', month: 'short', year: 'numeric' })
}

export const monthAndYearFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, { month: 'long', year: 'numeric' }, { capitalize: true })
}

export const dateAndTimeFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	})
}

export const shortDateAndTimePreciseFormatter = (locale: Locales, value: DateInput): string => {
	const date = parseDate(value)
	const isSameYear = date.getFullYear() === new Date().getFullYear()
	return formatDateIntl(date, locale, {
		day: 'numeric',
		month: 'short',
		year: isSameYear ? undefined : 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit'
	})
}

export const timestampFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, { hour: '2-digit', minute: '2-digit' })
}

export const timestampWithSecondsFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export const monthFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, { month: 'long' })
}

export const relativeFormatter = (locale: Locales, value: DateInput): string => {
	return formatDateIntl(parseDate(value), locale, { dateStyle: 'medium', timeStyle: 'short' })
}

export type AgoTranslations = {
	JustNow: () => string
	MinutesAgo: (args: { count: number }) => string
	HoursAgo: (args: { count: number }) => string
	DaysAgo: (args: { count: number }) => string
	MonthsAgo: (args: { count: number }) => string
	YearsAgo: (args: { count: number }) => string
}

export const agoFormatter = (
	locale: Locales,
	value: DateInput,
	getTranslations: (locale: Locales) => AgoTranslations
): string => {
	const translations = getTranslations(locale)
	const date = parseDate(value)
	const now = new Date()
	const diffMs = now.getTime() - date.getTime()

	// Handle future dates - treat as "just now"
	if (diffMs < 0) {
		return translations.JustNow()
	}

	const diffMins = Math.floor(diffMs / 60000)

	if (diffMins < 1) {
		return translations.JustNow()
	}

	if (diffMins < 60) {
		return translations.MinutesAgo({ count: diffMins })
	}

	const diffHours = Math.floor(diffMins / 60)
	if (diffHours < 24) {
		return translations.HoursAgo({ count: diffHours })
	}

	const diffDays = Math.floor(diffHours / 24)
	if (diffDays < 30) {
		return translations.DaysAgo({ count: diffDays })
	}

	const diffMonths = Math.floor(diffDays / 30)
	if (diffMonths < 12) {
		return translations.MonthsAgo({ count: diffMonths })
	}

	const diffYears = Math.floor(diffMonths / 12)
	return translations.YearsAgo({ count: diffYears })
}

export const customDateFormatter = (locale: Locales, input: CustomDateInput): string => {
	if (!input) return ''
	const date = parseDate(input.date)
	const dateFnsLocale = getDateFnsLocale(locale)
	let result = dFormatDate(date, input.formatString, { locale: dateFnsLocale })
	if (input.capitalize) result = capitalizeStr(result)
	return result
}

export const weightFormatter = (locale: Locales, value: Amount): string => {
	return (
		new Intl.NumberFormat(locale, {
			minimumFractionDigits: 0,
			maximumFractionDigits: 3
		}).format(amountToNumber(value)) + ' kg'
	)
}

// ============= Locale Utilities =============

export const baseLocale: Locales = 'en'

export const allLocales: Locales[] = ['en', 'da', 'is', 'nb', 'sv']

export const isLocale = (locale: string): locale is Locales => allLocales.includes(locale as Locales)
